// Page: EmailVerificationPending
// Purpose: Manages Firebase email verification flow, including applying action
// codes, refreshing profile, handling enterprise verification, and providing
// resend/check helpers with UX feedback.
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { auth } from '../../../firebase/firebase';
import { sendEmailVerification, signOut as firebaseSignOut, applyActionCode, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import { AlertTriangle } from 'lucide-react';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { SERVER_URL } from '../../../api/core';
import useDarkMode from '../../../hooks/useDarkMode';
import api from '../../../api';
import { fetchUserProfile } from '../../../context/auth';

// Function to clear all AI-related localStorage items (copied from PricingPage)
const clearAIStorageData = () => {
  console.log('Clearing any user-specific data from localStorage');
  localStorage.removeItem('refrral_ad_copy');
  localStorage.removeItem('refrral_ad_analysis');
  localStorage.removeItem('refrral_generated_release');
  
  // EasyWriter storage keys (if any are added in the future)
  localStorage.removeItem('refrral_easy_writer_data');
  
  console.log('Cleared all AI-related localStorage items');
};

export default function EmailVerificationPending() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, deleteCurrentUserAccount, setUser } = useAuth();
  const isDark = useDarkMode();
  const [isChecking, setIsChecking] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [lastCheckTime, setLastCheckTime] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [enterpriseData, setEnterpriseData] = useState(null);
  const [loadingEnterpriseData, setLoadingEnterpriseData] = useState(false);
  const hasAttemptedFetch = useRef(false);
  const verificationHandledRef = useRef(false);

  // Get enterprise and user info from navigation state or auth context
  const { enterprise: navEnterprise, isEnterprise, userEmail, userId } = location.state || {};
  const isEnterpriseUser = isEnterprise && navEnterprise;
  
  // Determine user information from various sources
  const currentUserId = userId || user?.uid;
  const currentUserEmail = userEmail || user?.email;
  const isAnyEnterpriseUser = user?.isEnterpriseUser || isEnterpriseUser;
  
  // Get enterprise data from user object, navigation state, or fetched data
  const enterprise = user?.enterprise || navEnterprise || enterpriseData;

  // Ensure Firebase auth persistence is set to LOCAL for maximum session retention
  useEffect(() => {
    const setupAuthPersistence = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('EmailVerificationPending: Firebase auth persistence set to LOCAL for maximum session retention');
      } catch (error) {
        console.error('EmailVerificationPending: Error setting auth persistence:', error);
      }
    };
    
    setupAuthPersistence();
  }, []);

  // Handle Firebase email verification URL parameters (guarded to run once)
  useEffect(() => {
    if (verificationHandledRef.current) return;
    const handleEmailVerification = async () => {
      const urlParams = new URLSearchParams(location.search);
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');
      
      if (mode === 'verifyEmail' && oobCode) {
        console.log('Processing email verification from URL parameters...');
        setIsChecking(true);
        try { sessionStorage.setItem('email_verification_in_progress', 'true'); } catch (_) {}
        
        try {
          // Ensure auth persistence before verification
          await setPersistence(auth, browserLocalPersistence);
          console.log('Auth persistence ensured before verification');
          
          // Apply the verification code
          await applyActionCode(auth, oobCode);
          console.log('Email verification code applied successfully');
          
          // Reload the user to get updated emailVerified status
          if (auth.currentUser) {
            await auth.currentUser.reload();
            console.log('User reloaded, emailVerified:', auth.currentUser.emailVerified);
          }
          
          // For enterprise users, complete the verification process on the server
          if (isAnyEnterpriseUser && currentUserId) {
            try {
              console.log('Completing enterprise verification on server...');
              await api.post('/enterprise/complete-verification', {
                userId: currentUserId
              });
              console.log('Enterprise verification completed on server');
              
              // Wait a moment for server-side updates to complete
              await new Promise(resolve => setTimeout(resolve, 1500));
            } catch (error) {
              console.error('Error completing enterprise verification on server:', error);
            }
          }
          
          // CRITICAL FIX: Refresh the user profile to get updated verification status
          console.log('Refreshing user profile after verification completion...');
          try {
            // Force refresh the profile from server
            const updatedUser = await fetchUserProfile(auth.currentUser, setUser, true);
            console.log('User profile refreshed after verification:', {
              uid: updatedUser?.uid,
              email: updatedUser?.email,
              isEnterpriseUser: updatedUser?.isEnterpriseUser,
              requiresEmailVerification: updatedUser?.requiresEmailVerification,
              enterpriseVerified: updatedUser?.enterpriseVerified,
              planLevel: updatedUser?.planLevel
            });
            
            // Mark completion and clear the URL parameters to clean up the URL
            try {
              sessionStorage.setItem('email_verification_completed_recently', 'true');
              sessionStorage.setItem('email_verification_completed_at', Date.now().toString());
            } catch (_) {}
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            
            // Navigate based on updated user data
            if (updatedUser?.planLevel === 'premium' || updatedUser?.isEnterprise) {
              console.log('Verified enterprise user, redirecting to app');
              try { sessionStorage.removeItem('email_verification_in_progress'); } catch (_) {}
              navigate('/app/', { replace: true });
            } else {
              console.log('Verified basic user, redirecting to pricing');
              try { sessionStorage.removeItem('email_verification_in_progress'); } catch (_) {}
              navigate('/pricing', { replace: true });
            }
            // Schedule clearing the completion flag shortly after navigation
            setTimeout(() => {
              try { sessionStorage.removeItem('email_verification_completed_recently'); } catch (_) {}
              try { sessionStorage.removeItem('email_verification_completed_at'); } catch (_) {}
            }, 5000);
          } catch (profileError) {
            console.error('Error refreshing user profile after verification:', profileError);
            // Continue with navigation using current user data as fallback
            console.log('Using current user data for navigation after profile refresh error');
            
            // Mark completion and clear the URL parameters to clean up the URL
            try {
              sessionStorage.setItem('email_verification_completed_recently', 'true');
              sessionStorage.setItem('email_verification_completed_at', Date.now().toString());
            } catch (_) {}
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            
            // Navigate to the appropriate page using current user data
            if (user?.planLevel === 'premium' || user?.isEnterprise) {
              console.log('Verified enterprise user (fallback), redirecting to app');
              try { sessionStorage.removeItem('email_verification_in_progress'); } catch (_) {}
              navigate('/app/', { replace: true });
            } else {
              console.log('Verified basic user (fallback), redirecting to pricing');
              try { sessionStorage.removeItem('email_verification_in_progress'); } catch (_) {}
              navigate('/pricing', { replace: true });
            }
            setTimeout(() => {
              try { sessionStorage.removeItem('email_verification_completed_recently'); } catch (_) {}
              try { sessionStorage.removeItem('email_verification_completed_at'); } catch (_) {}
            }, 5000);
          }
          
        } catch (error) {
          console.error('Error applying email verification code:', error);
          // If the code is invalid but the user is already verified, continue without showing error
          try {
            if (auth.currentUser) {
              await auth.currentUser.reload();
              if (auth.currentUser.emailVerified) {
                // Proceed with enterprise completion and navigation (same as success path)
                if (isAnyEnterpriseUser && currentUserId) {
                  try {
                    await api.post('/enterprise/complete-verification', { userId: currentUserId });
                    await new Promise(resolve => setTimeout(resolve, 1000));
                  } catch (_) {}
                }
                const updatedUser = await fetchUserProfile(auth.currentUser, setUser, true);
                try {
                  sessionStorage.setItem('email_verification_completed_recently', 'true');
                  sessionStorage.setItem('email_verification_completed_at', Date.now().toString());
                } catch (_) {}
                const dest = (updatedUser?.planLevel === 'premium' || updatedUser?.isEnterprise) ? '/app/' : '/pricing';
                const newUrl = window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
                navigate(dest, { replace: true });
                setTimeout(() => {
                  try { sessionStorage.removeItem('email_verification_completed_recently'); } catch (_) {}
                  try { sessionStorage.removeItem('email_verification_completed_at'); } catch (_) {}
                }, 5000);
                return;
              }
            }
          } catch (_) {}
          setError('Invalid or expired verification link. Please request a new verification email.');
        } finally {
          setIsChecking(false);
          try { sessionStorage.removeItem('email_verification_in_progress'); } catch (_) {}
        }
      }
    };
    
    handleEmailVerification();
    verificationHandledRef.current = true;
  }, [location.search, isAnyEnterpriseUser, currentUserId, user?.planLevel, user?.isEnterprise, navigate, setUser, user]);

  // Fetch enterprise data if we have an enterprise user but no enterprise data
  useEffect(() => {
    const fetchEnterpriseData = async () => {
      // Only fetch if we have an enterprise user but no enterprise data and we're not already loading
      if (isAnyEnterpriseUser && !enterprise && currentUserEmail && !loadingEnterpriseData && !enterpriseData && !hasAttemptedFetch.current) {
        console.log('Fetching enterprise data for user:', currentUserEmail);
        setLoadingEnterpriseData(true);
        hasAttemptedFetch.current = true;
        
        try {
          const emailDomain = currentUserEmail.split('@')[1];
          // Simplify: rely on server during complete-verification; skip redundant domain call
          console.log('Skipping domain lookup for performance; domain:', emailDomain);
        } catch (error) {
          console.error('Error fetching enterprise data:', error);
        } finally {
          setLoadingEnterpriseData(false);
        }
      }
    };

    fetchEnterpriseData();
    // Use ref to prevent duplicate fetches, so we only need basic dependencies
  }, [isAnyEnterpriseUser, currentUserEmail]);

  // Auto-check verification status every 5 seconds
  useEffect(() => {
    // Reduce load: do a quick check once after mount; no polling
    const timer = setTimeout(() => {
      checkVerificationStatus(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const checkVerificationStatus = async (isAutomatic = false) => {
    // Different debounce logic for manual vs automatic checks
    const now = Date.now();
    if (isAutomatic && now - lastCheckTime < 1000) {
      return; // Only debounce automatic checks
    }
    
    // Only show loading state for manual checks, not automatic ones
    if (!isAutomatic) {
      if (isChecking) return;
      setIsChecking(true);
      // Clear any existing messages
      setError('');
      setWarning('');
    }
    
    // Ensure at least 1 second delay for manual checks
    const startTime = Date.now();
    let emailVerified = false;
    
    try {
      setLastCheckTime(now);
      
      // Reload the current user to get fresh email verification status
      if (auth.currentUser) {
        await auth.currentUser.reload();
        
        if (auth.currentUser.emailVerified) {
          console.log('Email verified! Completing verification process...');
          emailVerified = true;
          
          // For enterprise users, complete the verification process
          if (isAnyEnterpriseUser && currentUserId) {
            try {
              console.log('Completing enterprise verification on server...');
              await api.post('/enterprise/complete-verification', {
                userId: currentUserId
              });
              console.log('Enterprise verification completed');
              
              // Wait a moment for server-side updates to complete
              await new Promise(resolve => setTimeout(resolve, 1500));
            } catch (error) {
              console.error('Error completing enterprise verification:', error);
            }
          }
          
          // CRITICAL FIX: Refresh the user profile to get updated verification status
          console.log('Refreshing user profile after verification completion...');
          try {
            // Force refresh the profile from server
            const updatedUser = await fetchUserProfile(auth.currentUser, setUser, true);
            console.log('User profile refreshed after verification:', {
              uid: updatedUser?.uid,
              email: updatedUser?.email,
              isEnterpriseUser: updatedUser?.isEnterpriseUser,
              requiresEmailVerification: updatedUser?.requiresEmailVerification,
              enterpriseVerified: updatedUser?.enterpriseVerified,
              planLevel: updatedUser?.planLevel
            });
            
            // Navigate based on updated user data
            if (updatedUser?.planLevel === 'premium' || updatedUser?.isEnterprise) {
              navigate('/app/', { replace: true });
            } else {
              navigate('/pricing', { replace: true });
            }
          } catch (profileError) {
            console.error('Error refreshing user profile after verification:', profileError);
            // Continue with navigation using current user data as fallback
            console.log('Using current user data for navigation after profile refresh error');
            
            // Navigate to the appropriate page using current user data
            if (user?.planLevel === 'premium' || user?.isEnterprise) {
              navigate('/app/', { replace: true });
            } else {
              navigate('/pricing', { replace: true });
            }
          }
          return;
        }
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      if (!isAutomatic) {
        setError('Error checking verification status. Please try again.');
      }
    } finally {
      // Only update loading state for manual checks
      if (!isAutomatic) {
        // Ensure at least 1 second has passed for better UX
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, 1000 - elapsedTime);
        
        setTimeout(() => {
          setIsChecking(false);
          
          // Show warning AFTER loading is complete and only if email is not verified
          if (!emailVerified) {
            setWarning('We have not yet received verification confirmation. If you verified your email but it\'s still not working, please contact support@advocacylab.io');
            // Clear warning after 8 seconds
            setTimeout(() => setWarning(''), 8000);
          }
        }, remainingTime);
      }
    }
  };

  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;
    
    try {
      setError('');
      setWarning('');
      if (auth.currentUser) {
        const actionCodeSettings = {
          url: `${window.location.origin}/auth-action`,
          handleCodeInApp: false,
        };
        
        await sendEmailVerification(auth.currentUser, actionCodeSettings);
        setShowSuccess(true);
        setResendCooldown(60); // 60 second cooldown
        
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        setError('No authenticated user found. Please try signing up again.');
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      
      // Handle specific error types
      if (error.code === 'auth/too-many-requests') {
        setError('Too many email requests. Please wait a few minutes before requesting another verification email.');
        setResendCooldown(300); // Set a 5-minute cooldown for too many requests
      } else if (error.message && error.message.includes('too-many-requests')) {
        setError('Too many email requests. Please wait a few minutes before requesting another verification email.');
        setResendCooldown(300); // Set a 5-minute cooldown for too many requests
      } else {
        setError('Failed to resend email. Please try again.');
      }
    }
  };

  // No auto-send here to avoid duplicate emails; user can resend manually if needed

  const handleSignOut = async () => {
    try {
      await firebaseSignOut(auth);
      
      // Redirect to appropriate sign-in page based on user type
      if (isAnyEnterpriseUser && enterprise) {
        navigate('/enterprise/signin', { 
          replace: true,
          state: { 
            enterprise, 
            domain: enterprise.enterpriseDomain 
          }
        });
      } else {
        navigate('/signin', { replace: true });
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle deleting account - exact copy from PricingPage
  const handleDeleteAccount = async () => {
    // Immediately clear all storage that could cause redirects
    sessionStorage.clear();
    localStorage.removeItem('auth_redirect_state');
    localStorage.removeItem('auth_redirect_destination');
    localStorage.removeItem('selectedPlan');
    localStorage.removeItem('just_signed_up');
    
    // Clear all Stripe-related items that could cause redirect to success page
    localStorage.removeItem('stripeSessionId');
    localStorage.removeItem('stripe_session_id');
    localStorage.removeItem('stripe_checkout_initiated');
    localStorage.removeItem('stripe_checkout_auth_state');
    localStorage.removeItem('stripe_checkout_return_path');
    localStorage.removeItem('post_login_redirect');
    localStorage.removeItem('post_login_session_id');
    localStorage.removeItem('stripe_auth_uid');
    localStorage.removeItem('stripe_auth_email');
    localStorage.removeItem('stripe_auth_timestamp');
    localStorage.removeItem('auth_for_checkout');
    localStorage.removeItem('stripe_auto_login_needed');
    sessionStorage.removeItem('in_stripe_checkout_flow');
    sessionStorage.removeItem('stripe_checkout_cancelled');
    
    // Set deletion flag
    setIsDeletingAccount(true);
    
    try {
      console.log("Starting account deletion and redirect to home...");
      
      // Get the user's ID token
      const idToken = await auth.currentUser.getIdToken();
      
      // Call the delete account API endpoint
      const response = await fetch(`${SERVER_URL}/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete account (${response.status})`);
      }
      
      // Clear AI-related localStorage data
      clearAIStorageData();
      
      // Sign out from Firebase directly (bypass AuthContext navigation)
      await firebaseSignOut(auth);
      
      // Clear user state immediately
      setUser(null);
      
      // Force immediate hard redirect to home page
      console.log("Account deleted, performing hard redirect to home page");
      window.location.href = '/';
      
    } catch (error) {
      console.error('Error deleting account:', error);
      // Even on error, force hard redirect to home
      console.log('Error occurred during account deletion, still performing hard redirect to home page');
      window.location.href = '/';
    }
  };

  const handleManualCheck = () => {
    checkVerificationStatus(false); // Manual check
  };

  const displayEmail = currentUserEmail || 'your email';
  const companyName = enterprise?.enterpriseName || 'your organization';

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  // Debug log for enterprise data
  console.log('EmailVerificationPending enterprise data:', {
    isAnyEnterpriseUser,
    enterprise,
    enterpriseImage: enterprise?.enterpriseImage,
    enterpriseName: enterprise?.enterpriseName,
    userEnterprise: user?.enterprise,
    userIsEnterpriseUser: user?.isEnterpriseUser,
    navEnterprise,
    fetchedEnterpriseData: enterpriseData,
    loadingEnterpriseData,
    currentUserEmail
  });

  // Debug log for SimpleLandingHeader props
  const headerProps = {
    showLogo: true, // Always show logo section
    enterpriseLogo: isAnyEnterpriseUser ? enterprise?.enterpriseImage : null,
    enterpriseName: isAnyEnterpriseUser ? enterprise?.enterpriseName : null
  };
  console.log('SimpleLandingHeader props:', headerProps);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header with theme toggle and sign out */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        onBackClick={handleSignOut}
        backButtonText="Sign out"
        isLoading={false}
        showLogo={headerProps.showLogo}
        enterpriseLogo={headerProps.enterpriseLogo}
        enterpriseName={headerProps.enterpriseName}
        showDelete={true}
        onDeleteClick={() => setShowDeleteConfirm(true)}
      />
      
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4 py-12">
        <div className="w-full max-w-lg">
          {/* Main content card */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl mx-auto flex items-center justify-center shadow-lg mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Check your email
              </h1>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                We sent a verification link to{' '}
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {displayEmail}
                </span>
                {isAnyEnterpriseUser && (
                  <>
                    <br />
                    <span className="text-sm">
                      Complete verification to access {companyName}'s account.
                    </span>
                  </>
                )}
              </p>
            </div>

            {/* Status indicator - static, no flashing */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Waiting for email verification
                </span>
              </div>
            </div>

            {/* Success message */}
            {showSuccess && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl p-4 mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-green-800 dark:text-green-200 text-sm font-medium">
                    Verification email sent successfully!
                  </p>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4 mb-6">
                <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
              </div>
            )}

            {/* Warning message */}
            {warning && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm">{warning}</p>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-4">
              {/* Resend email button */}
              <button
                onClick={handleResendEmail}
                disabled={resendCooldown > 0}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-xl transition-colors duration-200"
              >
                {resendCooldown > 0 ? (
                  `Resend in ${resendCooldown}s`
                ) : (
                  'Resend verification email'
                )}
              </button>

              {/* Check status button */}
              <button
                onClick={handleManualCheck}
                disabled={isChecking}
                className="w-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium py-3 px-4 rounded-xl transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-700"
              >
                {isChecking ? (
                  <span className="flex items-center justify-center">
                    <ButtonSpinner color="gray-600" className="mr-2" />
                    <span>Checking...</span>
                  </span>
                ) : (
                  'Check verification status'
                )}
              </button>
            </div>

            {/* Help text */}
            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center leading-relaxed">
                Can't find the email? Check your spam folder or contact{' '}
                <a href="mailto:support@advocacylab.io" className="text-purple-600 hover:text-purple-700 dark:text-purple-400">
                  support@advocacylab.io
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Delete account confirmation modal - exact copy from PricingPage */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-center text-red-500 mb-4">
              <AlertTriangle className="h-12 w-12" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
              Delete your account?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
              This action cannot be undone. All your data will be permanently deleted.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 disabled:opacity-70"
              >
                {isDeletingAccount ? (
                  <div className="flex items-center">
                    <ButtonSpinner color="white" />
                    <span className="ml-2">Deleting...</span>
                  </div>
                ) : (
                  "Delete account"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 